import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { type ActLlmStatusResponse, getActLlmStatus } from "@/services/api";
import { formatMessage, llmReviewMessages } from "@/services/messages";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, Check, Info, Loader2, X } from "lucide-react";

/**
 * Sprint 4 / A6.5a — иконка статуса LLM-ревизии акта в file-list.
 *
 * Семантика (фича 035 расширила):
 *  - ⚙️ pending      — есть pending блоки (LLM сейчас обрабатывает).
 *  - ✗ failed (red)  — есть failed (сетевые ошибки / некорректные ответы).
 *  - △ concern (amber) — skippedWithConcern > 0 (реально-проблемные пропуски,
 *    LLM не смог уверенно разобрать).
 *  - ✓ applied (green) — applied > 0, нет concerns/failed.
 *  - ℹ routine (slate) — skipped > 0, но все они штатные (no_coded_line).
 *  - (none)            — total === 0 или все блоки `none`.
 *
 * Polling: TanStack Query + `refetchInterval` 2с пока есть pending. Когда
 * pending=0, refetch отключается.
 *
 * Tooltip — shadcn `<Tooltip>` (mobile-friendly, не нативный `title`).
 *
 * Показывается ТОЛЬКО для completion_act файлов и только если total>0.
 */
export interface LlmStatusIconProps {
	fileId: number;
	fileType: string;
	fileStatus: string;
}

interface IconSpec {
	Icon: typeof Check;
	colorClass: string;
	tooltip: string;
	spinning?: boolean;
}

function pickIcon(d: ActLlmStatusResponse): IconSpec | null {
	const m = llmReviewMessages;
	if (d.total === 0) return null;

	if (d.pending > 0) {
		return {
			Icon: Loader2,
			colorClass: "text-amber-500",
			tooltip: formatMessage(m.icon_tooltip_pending, {
				applied: d.applied + d.skipped + d.failed,
				total: d.total,
			}),
			spinning: true,
		};
	}
	if (d.failed > 0) {
		return {
			Icon: X,
			colorClass: "text-red-500",
			tooltip: formatMessage(m.icon_tooltip_failed, { failed: d.failed, total: d.total }),
		};
	}
	if (d.skippedWithConcern > 0) {
		return {
			Icon: AlertTriangle,
			colorClass: "text-amber-600",
			tooltip: formatMessage(m.icon_tooltip_concern, {
				concern: d.skippedWithConcern,
				total: d.total,
			}),
		};
	}
	if (d.applied > 0) {
		return {
			Icon: Check,
			colorClass: "text-emerald-600",
			tooltip: formatMessage(m.icon_tooltip_applied, { applied: d.applied, total: d.total }),
		};
	}
	if (d.skipped > 0) {
		// Все skipped — штатные (no_coded_line). Ненавязчивый Info вместо «warning».
		return {
			Icon: Info,
			colorClass: "text-slate-400",
			tooltip: formatMessage(m.icon_tooltip_routine, { skipped: d.skipped, total: d.total }),
		};
	}
	// Все блоки `none` — LLM не запускался ни на один. Иконка не нужна.
	return null;
}

export function LlmStatusIcon({ fileId, fileType, fileStatus }: LlmStatusIconProps) {
	const enabled = fileType === "completion_act" && fileStatus === "parsed";

	const { data } = useQuery<ActLlmStatusResponse>({
		// queryKey v2 — после расширения ответа полем skippedWithConcern старый
		// кеш TanStack Query из v1 не подходит, сбрасываем.
		queryKey: ["llm-status", fileId, "v2"],
		queryFn: () => getActLlmStatus(fileId),
		enabled,
		refetchInterval: (query) => {
			const d = query.state.data;
			if (!d) return 2_000;
			return d.pending > 0 ? 2_000 : false;
		},
		staleTime: 0,
		retry: 2,
	});

	if (!enabled || !data) return null;
	const spec = pickIcon(data);
	if (!spec) return null;
	const { Icon, colorClass, tooltip, spinning } = spec;

	return (
		<TooltipProvider delayDuration={150}>
			<Tooltip>
				<TooltipTrigger asChild>
					<span className={`ml-1.5 inline-flex items-center ${colorClass}`} aria-label={tooltip}>
						<Icon className={`h-3.5 w-3.5 ${spinning ? "animate-spin" : ""}`} aria-hidden="true" />
					</span>
				</TooltipTrigger>
				<TooltipContent>{tooltip}</TooltipContent>
			</Tooltip>
		</TooltipProvider>
	);
}
